import React, { useEffect, useRef, useState } from 'react';
import { wordDiff } from '../../shared/wordDiff';
import { audioUrl } from './api';
import { studioHref } from './studioLink';
import { TakeRef } from './takeRef';

// Before / After of one result: two players on the same stretch of the take
// (context around the changed part included, so the joins are heard), links
// to the full tracks and to the studio; or the exact diff of a text.

const fmt = (s) => (s == null ? '' : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}.${String(Math.round((s % 1) * 10))}`);

// Plays [start, end] of a version. `end` null = to the end of the track.
// The bar seeks inside the range (click or drag, arrow keys when focused);
// play resumes from where it stopped, from the start once the range is over.
// `marks` [{t, title, lit}] — places on the bar (the remarks of a turn): a
// click plays from a moment before the place. A mark with `frac` instead of
// `t` (0..1 of the range) is an estimate, drawn dashed.
//
// A change of range or of source while the player is playing does not stop
// it: the new range (the stretch with the remarks once the timings landed)
// or the new source (the splice preview replacing the bare fragment) is
// adopted when the play ends or is paused. Marks move at once — they are
// drawn, not played. Idle, the player adopts the change right away.
const MARK_LEAD_SEC = 1;
// One player at a time on the Tasks screen: a player that starts tells the
// others, and a playing one pauses.
const PLAY_EVENT = 'qv:tasks-player-play';
// The speed is one for the whole screen (set on any player, every player
// follows) and is remembered in this browser.
const RATES = [0.75, 1, 1.25, 1.5, 2];
const RATE_KEY = 'tasks.playerRate';
const RATE_EVENT = 'qv:tasks-player-rate';
let sharedRate = (() => { try { const v = Number(localStorage.getItem(RATE_KEY)); return RATES.includes(v) ? v : 1; } catch (e) { return 1; } })();
function useSharedRate() {
  const [rate, setRateState] = useState(sharedRate);
  useEffect(() => {
    const on = () => setRateState(sharedRate);
    window.addEventListener(RATE_EVENT, on);
    return () => window.removeEventListener(RATE_EVENT, on);
  }, []);
  const setRate = (r) => {
    sharedRate = r;
    try { localStorage.setItem(RATE_KEY, String(r)); } catch (e) { /* a private window: the choice lives for the page */ }
    window.dispatchEvent(new CustomEvent(RATE_EVENT));
  };
  return [rate, setRate];
}
// `studio` {locationUuid, field}: the link at the right opens the take in Audio
// Studio; without it the raw track opens.
export function RangePlayer({ versionId, src = null, start = 0, end = null, label, onEnded, marks = null, studio = null }) {
  const ref = useRef(null);
  const barRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const playingRef = useRef(false);
  const [rate, setRate] = useSharedRate();
  // What the audio element plays now; the props may already say otherwise
  // (`pending`) while a play is on.
  const [live, setLive] = useState({ versionId, src, start: start || 0, end });
  const liveRef = useRef(live);
  liveRef.current = live;
  const pendingRef = useRef(null);
  const [pos, setPos] = useState(start || 0);
  const [dur, setDur] = useState(null);
  const sameSource = (a, b) => a.versionId === b.versionId && (a.src || null) === (b.src || null);
  const adopt = (next) => {
    const a = ref.current;
    const cur = liveRef.current;
    pendingRef.current = null;
    if (!sameSource(cur, next)) {
      if (a && !a.paused) a.pause();
      setPlaying(false); playingRef.current = false;
      setDur(null);
      setPos(next.start || 0);
    } else {
      // the same track, another stretch: stand at its start unless already inside it
      setPos((p) => (p < (next.start || 0) || (next.end != null && p > next.end) ? (next.start || 0) : p));
    }
    setLive(next);
  };
  useEffect(() => {
    const next = { versionId, src, start: start || 0, end };
    const cur = liveRef.current;
    if (sameSource(cur, next) && cur.start === next.start && cur.end === next.end) { pendingRef.current = null; return; }
    if (playingRef.current) pendingRef.current = next;
    else adopt(next);
  }, [versionId, src, start, end]); // eslint-disable-line react-hooks/exhaustive-deps
  const settle = () => { if (pendingRef.current) adopt(pendingRef.current); };
  useEffect(() => {
    const onOther = (e) => { const a = ref.current; if (a && e.detail !== a && !a.paused) { a.pause(); setPlaying(false); playingRef.current = false; settle(); } };
    window.addEventListener(PLAY_EVENT, onOther);
    return () => window.removeEventListener(PLAY_EVENT, onOther);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const a = ref.current; if (a) a.playbackRate = rate; }, [rate, live.versionId, live.src]);
  const lStart = live.start || 0;
  const stop = live.end != null ? live.end : dur;
  const span = stop != null ? Math.max(0.01, stop - lStart) : null;
  const clamp = (t) => Math.max(lStart, stop != null ? Math.min(t, stop) : t);
  const startPlaying = (a) => { a.playbackRate = rate; a.play().then(() => { setPlaying(true); playingRef.current = true; }).catch(() => { setPlaying(false); playingRef.current = false; }); };
  const play = () => {
    const a = ref.current;
    if (!a) return;
    if (playingRef.current) { a.pause(); setPlaying(false); playingRef.current = false; settle(); return; }
    const from = stop != null && pos >= stop - 0.05 ? lStart : clamp(pos);
    a.currentTime = from;
    setPos(from);
    startPlaying(a);
  };
  const finish = () => { setPlaying(false); playingRef.current = false; if (onEnded) onEnded(); settle(); };
  const onTime = () => {
    const a = ref.current;
    if (!a) return;
    setPos(a.currentTime);
    if (live.end != null && a.currentTime >= live.end) { a.pause(); finish(); }
  };
  // Seek to a point of the bar; start playing from there when idle.
  const seekTo = (clientX, wantPlay) => {
    const a = ref.current;
    const bar = barRef.current;
    if (!a || !bar || span == null) return;
    const r = bar.getBoundingClientRect();
    const t = clamp(lStart + Math.max(0, Math.min(1, (clientX - r.left) / Math.max(1, r.width))) * span);
    a.currentTime = t;
    setPos(t);
    if (wantPlay && !playingRef.current) startPlaying(a);
  };
  const onBarMouseDown = (e) => {
    e.preventDefault();
    seekTo(e.clientX, true);
    const move = (ev) => seekTo(ev.clientX, false);
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };
  const onBarKey = (e) => {
    const a = ref.current;
    if (!a || span == null) return;
    const step = e.shiftKey ? 5 : 1;
    let t = null;
    if (e.key === 'ArrowRight') t = clamp(pos + step);
    else if (e.key === 'ArrowLeft') t = clamp(pos - step);
    else if (e.key === 'Home') t = lStart;
    else if (e.key === 'End' && stop != null) t = stop;
    else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); play(); return; }
    if (t == null) return;
    e.preventDefault();
    a.currentTime = t;
    setPos(t);
  };
  const pct = span ? Math.max(0, Math.min(100, ((pos - lStart) / span) * 100)) : 0;
  const pending = pendingRef.current;
  return (
    <div className={`tk-player ${playing ? 'playing' : ''}`}>
      <audio ref={ref} src={live.src || audioUrl(live.versionId)} preload="metadata" onPlay={(e) => window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: e.currentTarget }))} onTimeUpdate={onTime} onLoadedMetadata={(e) => { setDur(e.target.duration); e.target.playbackRate = rate; }} onEnded={finish} />
      <button type="button" className="tk-play" onClick={play} aria-label={playing ? `Pause ${label}` : `Play ${label}`}>{playing ? <span className="tk-play-glyph pause">❚❚</span> : <span className="tk-play-glyph play">▶</span>}</button>
      <div className="tk-player-body">
        <div className="tk-player-label">{label} <span className="tk-muted">{live.versionId ? <><TakeRef id={live.versionId} /> · </> : ''}{fmt(lStart)}–{stop != null ? fmt(stop) : '…'}{pos > lStart + 0.05 ? ` · at ${fmt(pos)}` : ''}{pending ? ' · the new version is taken up after this play' : ''}</span></div>
        <div className="tk-bar" ref={barRef} role="slider" tabIndex={0} aria-label={`Position in ${label}`} aria-valuemin={lStart} aria-valuemax={stop != null ? stop : undefined} aria-valuenow={pos} onMouseDown={onBarMouseDown} onKeyDown={onBarKey} title="Click or drag to seek; arrows step 1 s, Shift+arrows 5 s">
          <div className="tk-bar-fill" style={{ width: `${pct}%` }} />
          <div className="tk-bar-knob" style={{ left: `${pct}%` }} />
          {span && marks ? marks.map((m) => (m.t != null ? m : { ...m, t: m.frac != null ? lStart + m.frac * span : null, rough: true })).filter((m) => m.t != null && m.t >= lStart && m.t <= stop).map((m) => (
            <button key={m.key || m.t} type="button" className={`tk-bar-mark ${m.lit ? 'lit' : ''} ${m.rough ? 'rough' : ''}`} style={{ left: `${((m.t - lStart) / span) * 100}%` }} title={`${m.title} — ${m.rough ? 'about' : 'at'} ${fmt(m.t)}${m.rough ? ' (an estimate: the timings are not measured yet)' : ''}; click to hear it`} aria-label={`Play from ${m.title}`}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); const a = ref.current; if (!a) return; const t = clamp(m.t - MARK_LEAD_SEC); a.currentTime = t; setPos(t); if (!playingRef.current) startPlaying(a); }} />
          )) : null}
        </div>
      </div>
      <select className="tk-rate" value={String(rate)} onChange={(e) => setRate(Number(e.target.value))} aria-label="Playback speed" title="Playback speed — one for every player on this screen">
        {RATES.map((r) => <option key={r} value={String(r)}>{r}×</option>)}
      </select>
      {live.versionId && studio && studioHref(studio.locationUuid, studio.field, live.versionId)
        ? <a className="tk-link" href={studioHref(studio.locationUuid, studio.field, live.versionId)} target="_blank" rel="noreferrer" title="Open this take in Audio Studio (new tab)">in studio ↗</a>
        : live.versionId
          ? <a className="tk-link" href={audioUrl(live.versionId)} target="_blank" rel="noreferrer" title="Open the full track">full track ↗</a>
          // a preview is not a stored take: no link, but the same room for one, so
          // the bars of a Before/After pair stay the same length
          : <span className="tk-link tk-link-slot" aria-hidden="true">in studio ↗</span>}
    </div>
  );
}

// Side by side, the words that changed highlighted: removed ones on the left,
// added ones on the right, everything else plain. Word diff from the `diff`
// package (the same one the server uses to measure an edit); the server's own
// parts are used when it sent them.
export function TextDiff({ before, after, diff }) {
  // Always recomputed here: the stored parts of older results were made by a
  // diff that split Cyrillic words into letters.
  const parts = wordDiff(before || '', after || '');
  const col = (side) => parts
    .filter((p) => (side === 'before' ? !p.a : !p.r))
    .map((p, i) => ((p.a || p.r) ? (side === 'before' ? <del key={i}>{p.v}</del> : <ins key={i}>{p.v}</ins>) : <span key={i}>{p.v}</span>));
  const changed = parts.filter((p) => p.a || p.r).length;
  return (
    <div className="tk-diff">
      <div className="tk-diff-col"><div className="tk-diff-head">Before</div><div className="tk-diff-text">{col('before')}</div></div>
      <div className="tk-diff-col"><div className="tk-diff-head">After <span className="tk-muted">· {changed} change{changed === 1 ? '' : 's'}</span></div><div className="tk-diff-text">{col('after')}</div></div>
    </div>
  );
}

// The text the players cover: context words (muted), the spliced words
// highlighted, context words; an ellipsis where the window cuts the text.
// Older results carry {turn, pieceStart, pieceEnd} instead.
export function VoicedText({ voiced, open = true, summary = 'Text as played — the re-voiced part is highlighted' }) {
  if (!voiced) return null;
  if (typeof voiced.piece === 'string') {
    return (
      <details className="tk-fold tk-voiced" open={open}>
        <summary>{summary}</summary>
        <div className="tk-voiced-text">
          {voiced.clippedLead ? <span className="tk-voiced-ctx">… </span> : null}
          <span className="tk-voiced-ctx">{voiced.lead}</span>
          <mark>{voiced.piece}</mark>
          <span className="tk-voiced-ctx">{voiced.tail}</span>
          {voiced.clippedTail ? <span className="tk-voiced-ctx"> …</span> : null}
        </div>
      </details>
    );
  }
  if (!voiced.turn) return null;
  const { turn, pieceStart, pieceEnd, prefix } = voiced;
  const has = Number.isInteger(pieceStart) && Number.isInteger(pieceEnd) && pieceEnd > pieceStart && !(pieceStart === 0 && pieceEnd >= turn.length);
  return (
    <details className="tk-fold tk-voiced" open={open}>
      <summary>Text as voiced{has ? ' — the spliced part is highlighted' : ''}</summary>
      <div className="tk-voiced-text">
        {prefix ? <span className="tk-voiced-prefix">{prefix}</span> : null}
        {has ? <>{turn.slice(0, pieceStart)}<mark>{turn.slice(pieceStart, pieceEnd)}</mark>{turn.slice(pieceEnd)}</> : turn}
      </div>
    </details>
  );
}

export default function BeforeAfter({ output, questId, studioHref }) {
  if (!output) return null;
  const o = output;
  if (o.kind === 'text') {
    return (
      <div className="tk-ba">
        <div className="tk-ba-title">Text edit · point {o.pointNo} · {o.field}</div>
        <TextDiff before={o.before} after={o.after} diff={o.diff} />
        {o.consequences && o.consequences.length ? <ul className="tk-consequences">{o.consequences.map((c, i) => <li key={i}>{c}</li>)}</ul> : null}
      </div>
    );
  }
  if (o.kind === 'revoice_pending') {
    return (
      <div className="tk-ba tk-ba-none">
        <div className="tk-ba-title">Point {o.pointNo} · {o.field}: waits for the re-voice pass</div>
        <div>{o.note}</div>
        {o.checks && o.checks.length ? <div className="tk-muted">{o.checks.map((c) => `${c.name}: ${c.note}`).join(' · ')}</div> : null}
      </div>
    );
  }
  if (o.kind === 'audio') {
    return (
      <div className="tk-ba">
        <div className="tk-ba-title">
          Audio · point {o.pointNo} · {o.field}
          {o.whole ? ' · full re-take' : ''}
          {studioHref ? <a className="tk-link" href={studioHref(o)} target="_blank" rel="noreferrer">open in Audio Studio ↗</a> : null}
        </div>
        {o.note ? <div className="tk-muted">{o.note}</div> : null}
        {o.before && o.before.versionId ? <RangePlayer versionId={o.before.versionId} start={o.before.start} end={o.before.end} label="Before" studio={o} /> : <div className="tk-muted">No earlier take to compare with.</div>}
        <RangePlayer versionId={o.after.versionId} start={o.after.start} end={o.after.end} label="After" studio={o} />
        {o.voiced ? <VoicedText voiced={o.voiced} /> : null}
        {o.turns && o.turns.length > 1 ? (
          <details className="tk-fold"><summary>{o.turns.length} turns spliced one after another</summary>
            {o.turns.map((t) => (
              <div key={t.turn} className="tk-turn-row">
                <div className="tk-muted">Turn {t.turn} · Speaker {t.speaker} · «{t.head}…»{t.spliced && t.spliced.kind !== 'turn' ? ` · spliced the ${t.spliced.kind} «${t.spliced.text}»` : ''}</div>
                {t.before && t.before.versionId ? <RangePlayer versionId={t.before.versionId} start={t.before.start} end={t.before.end} label="Before" studio={o} /> : null}
                <RangePlayer versionId={t.after.versionId} start={t.after.start} end={t.after.end} label="After" studio={o} />
                {t.voiced ? <VoicedText voiced={t.voiced} open={false} /> : null}
              </div>
            ))}
          </details>
        ) : null}
        {o.spliced ? (
          <div className="tk-muted">
            Spliced {o.spliced.kind === 'turn' ? 'the whole turn' : `the ${o.spliced.kind} «${o.spliced.text}»`}
            {o.spliced.fromStart != null ? <> ({fmt(o.spliced.fromStart)}–{fmt(o.spliced.fromEnd)} of {o.spliced.donor ? <>take <TakeRef id={o.spliced.donor.versionId} />, point {o.spliced.donor.pointNo}/{o.spliced.donor.field}</> : 'the re-voiced turn'})</> : ''}
          </div>
        ) : null}
        {o.turns && o.turns.length === 1 && o.turns[0].spliced && !o.spliced ? <div className="tk-muted">Spliced {o.turns[0].spliced.kind === 'turn' ? 'the whole turn' : `the ${o.turns[0].spliced.kind} «${o.turns[0].spliced.text}»`}</div> : null}
        {o.cut ? <div className="tk-muted">Cut «{o.cut.word}» at {fmt(o.cut.start)}–{fmt(o.cut.end)}</div> : null}
        {o.cuts ? <div className="tk-muted">{o.cuts.length} pause cut(s), −{o.removedSec}s</div> : null}
        {o.text ? (
          <details className="tk-fold" open><summary>Text change ({o.text.field}) {o.text.applyOnAccept ? '· applied on Accept' : '· proposed only'}</summary>
            <TextDiff before={o.text.before} after={o.text.after} />
            {o.text.note ? <div className="tk-muted">{o.text.note}</div> : null}
          </details>
        ) : null}
      </div>
    );
  }
  if (o.kind === 'none' || o.kind === 'skipped') {
    return <div className="tk-ba tk-ba-none"><div className="tk-ba-title">Point {o.pointNo}{o.field ? ` · ${o.field}` : ''}: {o.kind === 'skipped' ? 'skipped' : 'nothing changed'}</div><div>{o.note}</div></div>;
  }
  return null;
}
